/////////////////////////////////////////////////////////
// Configurations related to user.                     //
/////////////////////////////////////////////////////////

// The service, sector and band vocabularies are shared with the provider form
// (configListing.js) rather than duplicated — the registration document
// requires it, see configServiceTaxonomy.js.
import {
  serviceOptionsFor,
  FOOD_SECTORS,
  EMPLOYEE_BANDS,
  REVENUE_BANDS,
} from './configServiceTaxonomy';

// User FIELDS defined here are merged into the Console-hosted ones by
// mergeUserConfig in configHelpers.js — the same union that was already in
// place for listing fields. On a key clash the hosted definition wins, so a
// field can be promoted to Console later without touching this file.
//
// User TYPES are still hosted-only. The live ones are 'service-provider'
// (roles.provider) and 'food-processor' (roles.customer, cannot post
// listings); the array at the bottom of this file is inert and kept only so
// the ids are readable next to the fields that reference them.

/**
 * Configuration options for user fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be either 'public', 'protected', or 'private'.
 *                                  Default value: 'public'.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs on
 *                                  ProfileSettingsPage and AuthenticationPage.
 * - showConfig:                    Configuration for rendering user information. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - displayInProfile (optional):   Can be used to hide field content from profile page.
 *                                    Default value: true.
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for users to fill
 *   - requiredMessage (optional):    Message for mandatory fields.
 *   - displayInSignUp (optional):    Can be used to show field input on sign up page.
 *                                    Default value: true.
 * - userTypeConfig:                Configuration for limiting user field to specific user types.
 *   - limitToUserTypeIds:            Can be used to determine whether to limit the field to certain user types.
 *   - userTypeIds:                   An array of user types for which the extended
 *   (optional)                       data is relevant and should be added.
 */
const FOOD_PROCESSOR_ONLY = { limitToUserTypeIds: true, userTypeIds: ['food-processor'] };
const SERVICE_PROVIDER_ONLY = { limitToUserTypeIds: true, userTypeIds: ['service-provider'] };

// Required-field policy (client feedback round 2, 2026-09-11: "make all
// required fields mandatory before allowing users to proceed to the next
// section"). `isRequired: true` does three things at once — CustomExtendedDataField
// attaches a validator, the registration wizard refuses to leave the section
// until it passes, and util/onboardingStages.js lists the field on the hub
// checklist and holds the account at stage 3 until it is filled. Left optional
// on purpose: jobTitle (shared with providers, would become mandatory on their
// account page too), companyWebsite, productExamples, brandNames. referredBy
// (and its "Others" text) became required for both user types on 2026-10-09
// (client feedback 2026-10-07, item 1).
// The client's own form marks nothing optional except three documents, so the
// optional set is a judgement call — see docs/feedback-2026-09-11-round2-plan.md
// item 16 and flip a flag here if they want a different list.
export const userFields = [
  {
    // Section A of BOTH source forms — the Service Provider Vetting Form and
    // the Food Company Registration Form list an identical "Personal
    // Information" block, and job title was the only item in it without a home
    // in the code (docs/vetting-form-parity.md, "Gaps needing code" #1). Full
    // name, email and phone are all built-in Sharetribe user fields.
    //
    // Private, matching the sensitivity of the rest of Section A: it names the
    // contact person's role for vetting, it is not profile content. The operator
    // still reads it in Console, which is where vetting happens.
    //
    // Kept out of signup deliberately — that form is held to six fields (see
    // docs/onboarding-spec.md); this is collected during registration instead.
    key: 'jobTitle',
    scope: 'private',
    schemaType: 'shortText',
    showConfig: { label: 'Job title' },
    saveConfig: {
      label: 'Job title',
      placeholderMessage: 'e.g. Managing Director',
      // Required on BOTH forms: "Job title *" in Form 1 §01 and Form 2 §01
      // alike. It was optional here while the old document was the reference,
      // which is part of the client's "I am able to continue the form without
      // filling required fields" report — the field was there, but nothing
      // made it mandatory.
      isRequired: true,
      requiredMessage: 'Enter your job title.',
      displayInSignUp: false,
    },
    // Both user types: "A. Personal Information • Job title" appears verbatim in
    // the Food Company Registration Form too (source doc line ~203), and the
    // food-company form had no way to collect it.
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['service-provider', 'food-processor'],
    },
  },
  {
    // Country of incorporation — where the organisation is legally registered.
    //
    // This drives which vetting documents are asked for (see
    // configProfileDocuments.js): Ethiopia, Kenya, Senegal and Tanzania each
    // have their own registration/tax paperwork that replaces the general set.
    // Without this field every provider falls back to the general set.
    //
    // Deliberately distinct from the LISTING field of the same name: that one
    // says where a provider is based for search purposes, this one describes
    // the account and follows the person across any number of listings.
    // Option values are kept identical to the listing field so the two stay
    // interchangeable and so they match the `countries` arrays in
    // configProfileDocuments.js.
    key: 'country',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'Ethiopia', label: 'Ethiopia' },
      { option: 'Ghana', label: 'Ghana' },
      { option: 'Ivory Coast', label: 'Ivory Coast' },
      { option: 'Kenya', label: 'Kenya' },
      { option: 'Malawi', label: 'Malawi' },
      { option: 'Nigeria', label: 'Nigeria' },
      { option: 'Rwanda', label: 'Rwanda' },
      { option: 'Senegal', label: 'Senegal' },
      { option: 'South Africa', label: 'South Africa' },
      { option: 'Tanzania', label: 'Tanzania' },
      { option: 'Togo', label: 'Togo' },
      { option: 'Uganda', label: 'Uganda' },
      { option: 'Zambia', label: 'Zambia' },
      // Thirteen countries — the PFS programme geography — plus a catch-all.
      // Client decision 2026-09-16, when asked whether to widen this to a
      // global searchable multi-select: "I would say 13 country list."
      //
      // Without 'Other' an organisation based outside the thirteen could not
      // register at all. documentsForUser() has no country-specific set for it,
      // so they get the general registration documents — the correct fallback.
      //
      // Kept byte-identical to the two listing enums (`country` and
      // `countriesServed` in configListing.js), including order: Togo used to
      // sit last here because it was appended after the rest, which made the
      // dropdown read differently on the account page than in the listing
      // wizard for no reason.
      { option: 'Other', label: 'Other' },
    ],
    showConfig: {
      label: 'Country of primary operation',
    },
    saveConfig: {
      // Form 2 §02's wording. Form 1 §02 calls the provider's equivalent
      // "Headquarters country" and collects it on the listing; this account-
      // level field is the one that decides which country-specific documents
      // are asked for, and is true of both user types.
      label: 'Country of primary operation',
      placeholderMessage: 'Select a country…',
      isRequired: true,
      requiredMessage: 'Select your country of primary operation.',
      displayInSignUp: true,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['service-provider', 'food-processor'],
    },
  },

  ///////////////////////////////////////////////////////////////////////
  // FORM 2 — FOOD BUSINESS REGISTRATION                                //
  //                                                                    //
  // Source: docs/source-docs/pfs-registration-forms-2026-09.md, §01-§08.//
  // That document REPLACES the "Food Company Registration Form" half of //
  // docs/source-docs/Service Provider Vetting Form.txt. Fields that     //
  // existed only in the old document — ownershipType, productionCapacity,
  // productExamples, productionVolumes, brandNames, currentCertifications,
  // supportMethodPreference, the primary-contact trio and the business  //
  // registration number — are gone rather than carried alongside the new //
  // ones: extra fields ARE the misalignment the client keeps reporting.  //
  // Removing a key from this config stops it rendering; it does not     //
  // delete anything already stored on a profile.                        //
  //                                                                    //
  // Where each section lives:                                          //
  //   §01 Your Details ............ built-in name/email/phone +        //
  //                                 `jobTitle` above (shared with       //
  //                                 providers — Form 1 §01 is the same  //
  //                                 block, which is why it is not       //
  //                                 scoped to one user type).           //
  //   §02 About Your Business ..... `business_name` (hosted) + `country`//
  //                                 above, then the fields below.       //
  //   §03 Products & Markets ...... below.                             //
  //   §04 Support Needed .......... `businessNeeds`, drawn from the SAME//
  //                                 taxonomy as the provider's          //
  //                                 `serviceAreas` — see                //
  //                                 configServiceTaxonomy.js.           //
  //   §05 Business Growth ......... below, private (the document marks  //
  //                                 both revenue questions confidential).//
  //   §06 PFS Relationship ........ below, private.                    //
  //   §07 Documents ............... configProfileDocuments.js.         //
  //   §08 Declaration & Consent ... OnboardingPage/SubmitRegistrationForm.
  //                                                                    //
  // Every field: displayInSignUp: false (signup stays the six fields it //
  // has today) and userTypeConfig limited to 'food-processor'. Under    //
  // jest this trips a harmless "Unsupported user extended data          //
  // configurations detected (userTypeConfig)" warning — the hosted      //
  // user-types asset isn't loaded there, so the scoping check degrades  //
  // to unscoped. Cosmetic only.                                         //
  //                                                                    //
  // Scope: descriptive/capability facts are 'public', confidential ones //
  // are 'private'. That split is not only about privacy —               //
  // util/onboardingStages.js's hasStartedRegistration reads             //
  // `Object.keys(publicData).length > SIGNUP_KEYS.length` to decide     //
  // whether a food business has touched their profile yet. If every     //
  // field below were private, filling in the whole form would still     //
  // read as "not started" until a document was uploaded.                //
  ///////////////////////////////////////////////////////////////////////

  // === §02. About Your Business =========================================
  {
    key: 'companyState',
    scope: 'public',
    schemaType: 'shortText',
    showConfig: { label: 'City / State / Region' },
    saveConfig: {
      label: 'City / State / Region',
      placeholderMessage: 'e.g. Nairobi, Nairobi County',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: FOOD_PROCESSOR_ONLY,
  },
  {
    key: 'companyWebsite',
    scope: 'public',
    schemaType: 'shortText',
    showConfig: { label: 'Website' },
    saveConfig: {
      label: 'Website',
      placeholderMessage: 'https://…',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: FOOD_PROCESSOR_ONLY,
  },
  {
    key: 'yearEstablished',
    scope: 'public',
    schemaType: 'long',
    numberConfig: { minimum: 1900, maximum: 2100 },
    showConfig: { label: 'Year established' },
    saveConfig: { label: 'Year established', isRequired: false, displayInSignUp: false },
    userTypeConfig: FOOD_PROCESSOR_ONLY,
  },
  {
    key: 'businessType',
    scope: 'public',
    schemaType: 'enum',
    // The five options of Form 2 §02. The previous list mixed legal form with
    // activity type ("processor", "exporter", …); the document asks only for
    // legal form, and the activity question is §03's value chains.
    enumOptions: [
      { option: 'sole-proprietor', label: 'Sole Proprietor' },
      { option: 'limited-liability-company', label: 'Limited Liability Company' },
      { option: 'cooperative', label: 'Cooperative' },
      { option: 'partnership', label: 'Partnership' },
      { option: 'other', label: 'Other' },
    ],
    showConfig: { label: 'Business type' },
    saveConfig: {
      label: 'Business type',
      placeholderMessage: 'Select an option…',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: FOOD_PROCESSOR_ONLY,
  },
  {
    key: 'numberOfEmployees',
    scope: 'public',
    schemaType: 'enum',
    // Banded, not a raw integer: the document asks for a band, and a band is
    // what can be filtered and reported on. Same five bands the provider form
    // uses for its own headcount question.
    enumOptions: EMPLOYEE_BANDS,
    showConfig: { label: 'Number of employees' },
    saveConfig: {
      label: 'Number of employees',
      placeholderMessage: 'Select a range…',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: FOOD_PROCESSOR_ONLY,
  },

  // === §03. Products & Markets ==========================================
  {
    key: 'valueChains',
    scope: 'public',
    schemaType: 'multi-enum',
    // The same sixteen leaves the provider picks as `industriesServed` in
    // Form 1 §04. Shared so "provider has worked in dairy" and "this business
    // is a dairy" are the same value and can be matched.
    enumOptions: FOOD_SECTORS,
    showConfig: { label: 'Value chains' },
    saveConfig: {
      label: 'Which value chain(s) best describe your core business?',
      placeholderMessage: 'Select all that apply…',
      isRequired: true,
      displayInSignUp: false,
    },
    userTypeConfig: FOOD_PROCESSOR_ONLY,
  },
  {
    key: 'productsOffered',
    scope: 'public',
    schemaType: 'text',
    showConfig: { label: 'Main finished products' },
    saveConfig: {
      label: 'List your main finished products',
      placeholderMessage: 'e.g. maize flour, fortified porridge blends, packaged juices',
      isRequired: true,
      displayInSignUp: false,
    },
    userTypeConfig: FOOD_PROCESSOR_ONLY,
  },
  {
    key: 'marketsServed',
    scope: 'public',
    schemaType: 'multi-enum',
    // Was free text. The document gives three fixed options, and "Export" is
    // exactly the kind of answer a provider offering Export & Market Access
    // should be matchable against — which free text cannot do.
    enumOptions: [
      { option: 'local', label: 'Local' },
      { option: 'regional', label: 'Regional' },
      { option: 'export', label: 'Export' },
    ],
    showConfig: { label: 'Markets served' },
    saveConfig: {
      label: 'Markets served',
      placeholderMessage: 'Select all that apply…',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: FOOD_PROCESSOR_ONLY,
  },

  // === §04. Support Needed ==============================================
  // The document's note on this section: "Use the same service categories as
  // the Service Provider Registration Form. This enables direct marketplace
  // matching." Hence serviceOptionsFor('business') rather than a list
  // maintained here — the provider side reads the same table.
  {
    key: 'businessNeeds',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: serviceOptionsFor('business'),
    showConfig: { label: 'Support needed' },
    saveConfig: {
      label: 'What support do you need?',
      placeholderMessage: 'Select all that apply…',
      isRequired: true,
      displayInSignUp: false,
    },
    userTypeConfig: FOOD_PROCESSOR_ONLY,
  },
  {
    key: 'needsPriority',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'immediately', label: 'Immediately' },
      { option: 'within-3-months', label: 'Within 3 months' },
      { option: 'within-6-months', label: 'Within 6 months' },
      { option: 'longer-term', label: 'Longer term' },
    ],
    showConfig: { label: 'Timeframe' },
    saveConfig: {
      label: 'How quickly do you need these services?',
      placeholderMessage: 'Select an option…',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: FOOD_PROCESSOR_ONLY,
  },

  // === §05. Business Growth =============================================
  // Private: the document marks both questions "(confidential)". Private user
  // data is readable by the member and by operators in Console — where vetting
  // and matching happen — and never rendered on a public profile.
  {
    key: 'currentAnnualRevenue',
    scope: 'private',
    schemaType: 'enum',
    enumOptions: REVENUE_BANDS,
    showConfig: { label: 'Current annual revenue' },
    saveConfig: {
      label: 'Current annual revenue — most recent financial year (confidential)',
      placeholderMessage: 'Select a range…',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: FOOD_PROCESSOR_ONLY,
  },
  {
    key: 'targetAnnualRevenue',
    scope: 'private',
    schemaType: 'enum',
    enumOptions: REVENUE_BANDS,
    showConfig: { label: 'Target annual revenue in 3 years' },
    saveConfig: {
      label: 'Target annual revenue in 3 years (confidential)',
      placeholderMessage: 'Select a range…',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: FOOD_PROCESSOR_ONLY,
  },

  // === §06. PFS Relationship ============================================
  // Private: programme administration, not profile content.
  {
    key: 'isCurrentPfsClient',
    scope: 'private',
    schemaType: 'boolean',
    showConfig: { label: 'Current PFS client' },
    saveConfig: {
      label: 'Are you currently a PFS client?',
      isRequired: true,
      displayInSignUp: false,
    },
    userTypeConfig: FOOD_PROCESSOR_ONLY,
  },
  {
    key: 'interestedInVolunteerExperts',
    scope: 'private',
    schemaType: 'boolean',
    showConfig: { label: 'Open to PFS volunteers and technical partners' },
    saveConfig: {
      label:
        'Are you open to receiving support from PFS corporate volunteers and technical partners?',
      isRequired: true,
      displayInSignUp: false,
    },
    userTypeConfig: FOOD_PROCESSOR_ONLY,
  },
  {
    // Form 2 §06's third question. Key kept as `referredBy` so answers already
    // given are not orphaned.
    //
    // A dropdown as of client feedback 2026-10-07 (item 1, "highest priority"):
    // the options are the client's list, in the client's words. A free-text
    // answer stored before this change no longer matches an option, so the
    // select shows its placeholder until the member picks one; nothing is
    // deleted until they save.
    //
    // The old `interestedInEcosystemPartners` field is gone: Form 2 asks that
    // as a §08 declaration ("I consent to being matched with relevant service
    // providers and ecosystem partners"), which SubmitRegistrationForm
    // already collects, so keeping it here asked the same question twice.
    //
    // Asked of BOTH user types and required (no userTypeConfig, isRequired):
    // food companies answer it in registration §06, service providers on the
    // wizard's Contact tab (and on their profile settings). Required means the
    // registration wizard, the provider's Publish and the onboarding hub all
    // hold until it is answered — the client wants this answer from everyone.
    key: 'referredBy',
    scope: 'private',
    schemaType: 'enum',
    enumOptions: [
      { option: 'google', label: 'Google' },
      { option: 'facebook', label: 'Facebook' },
      { option: 'twitter', label: 'Twitter' },
      { option: 'youtube', label: 'YouTube' },
      { option: 'friends-family', label: 'Friends/Family' },
      { option: 'other', label: 'Others' },
    ],
    showConfig: { label: 'How they heard about us' },
    saveConfig: {
      label: 'How did you hear about us?',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      displayInSignUp: false,
    },
  },
  {
    // The free-text half of the question above, shown only while "Others" is
    // selected ("selecting this should allow the user to enter a custom
    // response"). `showWhen` is read by isUserFieldHidden (util/userHelpers.js):
    // the registration wizard and the account page hide the input and leave it
    // out of their required-field read-outs, and both save paths null it when
    // the answer goes back to a listed option, so a stale "other" text never
    // lingers in privateData.
    //
    // Required, but only ever enforced while it is shown: every required-field
    // check skips a hidden conditional field (isUserFieldHidden), so a member
    // who picked Google is never asked for it, and one who picked Others
    // cannot continue with it blank.
    key: 'referredByOther',
    scope: 'private',
    schemaType: 'shortText',
    showWhen: { fieldKey: 'referredBy', equals: 'other' },
    showConfig: { label: 'How they heard about us (other)' },
    saveConfig: {
      label: 'Please tell us how you heard about us',
      placeholderMessage: 'e.g. a PFS event, a partner organisation, a web search',
      isRequired: true,
      displayInSignUp: false,
    },
  },
];

/////////////////////////////////////
// User types (hosted-authoritative)//
/////////////////////////////////////
/**
 * NOT merged — mergeUserConfig takes user types from the Console asset only.
 * Listed here so the ids used in userTypeConfig above are readable in context.
 *
 * service-provider — roles: { provider: true },  postListings: true
 * food-processor   — roles: { customer: true },  postListings: false
 */
export const userTypes = [
  {
    userType: 'service-provider',
    label: 'Service Provider',
  },
  {
    userType: 'food-processor',
    label: 'Food processor',
  },
];
